import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdminProtected from '../components/AdminProtected';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Upload, 
  FileSpreadsheet, 
  Users, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Download,
  Loader2,
  School,
  RefreshCw,
  FileText,
  Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// School options - must match SCHOOLS_DATA in backend/exam_data.py
const SCHOOLS = [
  { id: 'the-word', name: 'School of the Word', language: 'en' },
  { id: 'solid-foundation', name: 'School of Solid Foundation', language: 'en' },
  { id: 'victorious-living', name: 'School of Victorious Living', language: 'en' },
  { id: 'evangelism', name: 'School of Evangelism', language: 'en' },
  { id: 'apologetics', name: 'School of Apologetics', language: 'en' },
  { id: 'la-parole', name: "École de la Parole", language: 'fr' },
  { id: 'fondation-solide', name: "École de la Fondation Solide", language: 'fr' },
  { id: 'vie-victorieuse', name: "École de la Vie Victorieuse", language: 'fr' },
  { id: 'evangelisation', name: "École d'Évangélisation", language: 'fr' },
  { id: 'apologetique', name: "École d'Apologétique", language: 'fr' }
];

const BulkImportContent = () => {
  const navigate = useNavigate();
  const [selectedSchool, setSelectedSchool] = useState('');
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [allParsedStudents, setAllParsedStudents] = useState([]); // Store ALL parsed students for import
  const [totalStudents, setTotalStudents] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);
  const [parseError, setParseError] = useState(null);

  // File drop handler
  const onDrop = useCallback(async (acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    if (!uploadedFile) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    
    if (!validTypes.includes(uploadedFile.type) && !uploadedFile.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error('Please upload an Excel (.xlsx, .xls) or CSV file');
      return;
    }

    setFile(uploadedFile);
    setParseError(null);
    setImportResult(null);

    // Parse file for preview
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);

      const response = await fetch(`${BACKEND_URL}/api/admin/parse-student-file`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewData(data.students.slice(0, 10)); // Show first 10 rows for preview
        setAllParsedStudents(data.students); // Store ALL students for import
        setTotalStudents(data.total);
        toast.success(`Found ${data.total} students in file`);
      } else {
        const error = await response.json();
        setParseError(error.detail || 'Failed to parse file');
        toast.error('Failed to parse file');
      }
    } catch (error) {
      setParseError('Error reading file');
      toast.error('Error reading file');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv']
    },
    maxFiles: 1
  });

  // Handle bulk import
  const handleImport = async () => {
    if (!file || !selectedSchool) {
      toast.error('Please select a school and upload a file');
      return;
    }

    if (allParsedStudents.length === 0) {
      toast.error('No students found in the file. Please upload a valid file.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setImportResult(null);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 300);

      // Send the parsed student data as JSON
      const response = await fetch(`${BACKEND_URL}/api/admin/bulk-enroll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          students: allParsedStudents,
          school_id: selectedSchool,
          default_password: 'Laikos2024'
        })
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        const result = await response.json();
        setImportResult(result);
        toast.success(`Successfully imported ${result.enrolled_in_school || result.enrolled_count || 0} students!`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Import failed');
        setImportResult({ error: error.detail || 'Import failed' });
      }
    } catch (error) {
      toast.error('Import failed: ' + error.message);
      setImportResult({ error: error.message });
    } finally {
      setIsUploading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setFile(null);
    setPreviewData([]);
    setAllParsedStudents([]);
    setTotalStudents(0);
    setImportResult(null);
    setParseError(null);
    setUploadProgress(0);
  };

  // Download template
  const downloadTemplate = () => {
    const csvContent = "Name,Email,Phone,Branch,Constituency\nJohn Doe,john@example.com,+1234567890,Newark,NEW JERSEY/NEW YORK\nJane Smith,jane@example.com,+0987654321,Boston,NEW ENGLAND";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 py-8 pt-20">
        {/* Back Link */}
        <Link 
          to="/statistics" 
          className="inline-flex items-center gap-2 text-[#5f7c8a] hover:text-[#4a6470] mb-6"
        >
          <ArrowLeft size={20} />
          Back to Statistics
        </Link>

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Users className="text-[#5f7c8a]" />
            Bulk Student Import
          </h1>
          <p className="text-gray-600 mt-2">
            Import multiple students at once from Excel or CSV files
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Upload Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* School Selection */}
            <Card className="border-2 border-blue-200">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
                <CardTitle className="text-lg flex items-center gap-2">
                  <School className="text-blue-500" />
                  Select School
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <select
                  value={selectedSchool}
                  onChange={(e) => setSelectedSchool(e.target.value)}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="school-select"
                >
                  <option value="">-- Select a school --</option>
                  {SCHOOLS.map(school => (
                    <option key={school.id} value={school.id}>
                      {school.name} ({school.language === 'en' ? 'English' : 'French'})
                    </option>
                  ))}
                </select>
              </CardContent>
            </Card>

            {/* File Upload Area */}
            <Card className="border-2 border-green-200">
              <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Upload className="text-green-500" />
                  Upload Student File
                </CardTitle>
                <CardDescription>
                  Drag & drop or click to upload Excel (.xlsx) or CSV file
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
                    ${isDragActive ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-green-400 hover:bg-green-50/50'}
                    ${file ? 'border-green-500 bg-green-50' : ''}`}
                >
                  <input {...getInputProps()} data-testid="file-input" />
                  
                  {file ? (
                    <div className="space-y-3">
                      <FileSpreadsheet size={48} className="mx-auto text-green-500" />
                      <p className="font-medium text-gray-800">{file.name}</p>
                      <p className="text-sm text-gray-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); handleReset(); }}
                        className="text-red-500 border-red-300 hover:bg-red-50"
                      >
                        <Trash2 size={14} className="mr-1" /> Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Upload size={48} className="mx-auto text-gray-400" />
                      <p className="text-gray-600">
                        {isDragActive ? 'Drop the file here...' : 'Drag & drop file here, or click to browse'}
                      </p>
                      <p className="text-sm text-gray-400">
                        Supports .xlsx, .xls, .csv files
                      </p>
                    </div>
                  )}
                </div>

                {parseError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                    <AlertTriangle size={18} />
                    {parseError}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Preview Table */}
            {previewData.length > 0 && (
              <Card className="border-2 border-purple-200">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="text-purple-500" />
                    Preview (First 10 of {totalStudents} students)
                  </CardTitle>
                  <CardDescription>
                    {totalStudents > 10 && `${totalStudents - 10} more students will also be imported`}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">#</TableHead>
                          <TableHead className="text-xs">Name</TableHead>
                          <TableHead className="text-xs">Email</TableHead>
                          <TableHead className="text-xs">Phone</TableHead>
                          <TableHead className="text-xs">Branch</TableHead>
                          <TableHead className="text-xs">Constituency</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewData.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell className="text-xs text-gray-500">{index + 1}</TableCell>
                            <TableCell className="text-xs font-medium">{row.name}</TableCell>
                            <TableCell className="text-xs">{row.email}</TableCell>
                            <TableCell className="text-xs">{row.phone || '-'}</TableCell>
                            <TableCell className="text-xs">{row.branch || '-'}</TableCell>
                            <TableCell className="text-xs">
                              {row.constituency ? (
                                <Badge variant="outline" className="text-xs">
                                  {row.constituency}
                                </Badge>
                              ) : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Progress */}
            {isUploading && (
              <Card className="border-2 border-amber-200">
                <CardContent className="py-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Importing students...</span>
                      <span className="text-sm text-gray-500">{uploadProgress}%</span>
                    </div>
                    <Progress value={uploadProgress} className="h-3" />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Import Result */}
            {importResult && !importResult.error && (
              <Card className="border-2 border-green-300 bg-green-50">
                <CardContent className="py-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle size={40} className="text-green-500 flex-shrink-0" />
                    <div className="space-y-2 flex-1">
                      <h3 className="font-semibold text-green-800 text-lg">Import Successful!</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="bg-white p-3 rounded-lg">
                          <p className="text-gray-500">New Students Created</p>
                          <p className="text-2xl font-bold text-green-600">{importResult.created_users || 0}</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg">
                          <p className="text-gray-500">Students Enrolled</p>
                          <p className="text-2xl font-bold text-blue-600">{importResult.enrolled_in_school || 0}</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg">
                          <p className="text-gray-500">Skipped (Already Enrolled)</p>
                          <p className="text-2xl font-bold text-amber-600">{importResult.skipped || 0}</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg">
                          <p className="text-gray-500">Errors</p>
                          <p className="text-2xl font-bold text-red-600">{importResult.errors?.length || 0}</p>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-4 pt-4 border-t border-green-200">
                        <Button
                          onClick={() => {
                            // Determine if French or English school
                            const frenchSchools = ['la-parole', 'fondation-solide', 'vie-victorieuse', 'evangelisation', 'apologetique'];
                            const isFrench = frenchSchools.includes(selectedSchool);
                            navigate(isFrench ? `/ecole/${selectedSchool}` : `/school/${selectedSchool}`);
                          }}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <School size={16} className="mr-2" />
                          View School Details
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => navigate('/statistics')}
                          className="border-green-300 text-green-700 hover:bg-green-100"
                        >
                          View Statistics
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {importResult && importResult.error && (
              <Card className="border-2 border-red-300 bg-red-50">
                <CardContent className="py-6">
                  <div className="flex items-center gap-4">
                    <XCircle size={40} className="text-red-500" />
                    <div>
                      <h3 className="font-semibold text-red-800">Import Failed</h3>
                      <p className="text-red-600">{importResult.error}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4">
              <Button
                onClick={handleImport}
                disabled={!file || !selectedSchool || isUploading}
                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                size="lg"
                data-testid="import-btn"
              >
                {isUploading ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload size={18} className="mr-2" />
                    Import Students
                  </>
                )}
              </Button>
              
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={isUploading}
                size="lg"
              >
                <RefreshCw size={18} className="mr-2" />
                Reset
              </Button>
            </div>
          </div>

          {/* Right Column - Instructions */}
          <div className="space-y-6">
            {/* Template Download */}
            <Card className="border-2 border-amber-200">
              <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Download className="text-amber-500" />
                  Download Template
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <p className="text-sm text-gray-600 mb-4">
                  Download a sample template to see the required format for student data.
                </p>
                <Button 
                  variant="outline" 
                  onClick={downloadTemplate}
                  className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  <Download size={16} className="mr-2" />
                  Download CSV Template
                </Button>
              </CardContent>
            </Card>

            {/* Instructions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Instructions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-gray-600">
                <div className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  <p>Select the school where students will be enrolled</p>
                </div>
                <div className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  <p>Upload an Excel or CSV file with student data</p>
                </div>
                <div className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  <p>Review the preview to ensure data is correct</p>
                </div>
                <div className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                  <p>Click "Import Students" to process the file</p>
                </div>
              </CardContent>
            </Card>

            {/* Required Columns */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Required Columns</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-500" />
                    <span><strong>Name</strong> - Full name</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-500" />
                    <span><strong>Email</strong> - Email address</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-[14px]" />
                    <span className="text-gray-500"><strong>Phone</strong> - Optional</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-[14px]" />
                    <span className="text-gray-500"><strong>Branch</strong> - Optional</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-[14px]" />
                    <span className="text-gray-500"><strong>Constituency</strong> - Optional</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Default Password Info */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-800">Default Password</p>
                    <p className="text-blue-600 mt-1">
                      All imported students will be assigned the default password: <strong>Laikos2024</strong>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

const BulkImportPage = () => {
  return (
    <AdminProtected title="Bulk Student Import">
      <BulkImportContent />
    </AdminProtected>
  );
};

export default BulkImportPage;
